if ( typeof CutoffNotice !== 'function' ) {

  class CutoffNotice extends HTMLElement {

    connectedCallback() {
      this._evaluate();
      document.addEventListener('deliveryConfigSaved', (e) => {
        if ( !e.detail?.skipCutoffNotice ) this._evaluate();
      });
    }

    _evaluate() {
      const config = this._loadDeliveryConfig();
      if ( !config?.locationId ) return this._hide();

      const selectedToday  = config.date === this._toMiamiDate(0);
      const autoSwitched   = config.autoSwitchedFromToday === true;

      // Only relevant if today was (or still is) the selected date
      if ( !selectedToday && !autoSwitched ) return this._hide();

      const locations = window.KROWN?.settings?.pickup_locations;
      if ( !locations ) return this._hide();

      const location = locations.find(l => String(l.location_id) === String(config.locationId));
      if ( !location?.close_time ) return this._hide();

      const closeMin = this._parseTimeToMinutes(location.close_time);
      if ( closeMin === null ) return this._hide();

      const cutoffMin = closeMin - 60; // 1 hour before closing
      if ( this._nowMiamiMinutes() < cutoffMin ) return this._hide();

      // Past cutoff — switch only if not already switched
      if ( selectedToday ) this._switchToTomorrow(config);
      this._show();
    }

    _show() {
      this.style.display = '';
      const message = window.KROWN.settings.locales.cutoff_notice_message;
      if ( this.hasAttribute('data-show-alert') ) {
        this.innerHTML = `<div class="alert alert--wide alert--warning">${message}</div>`;
      } else {
        this.innerHTML = message;
      }
    }

    _hide() {
      this.style.display = 'none';
      this.innerHTML = '';
    }

    async _switchToTomorrow(config) {
      const updated = { ...config, date: this._toMiamiDate(1), autoSwitchedFromToday: true };

      // Update localStorage immediately so the header and other reads reflect the change
      localStorage.setItem('domaselo_delivery_config', JSON.stringify(updated));

      // Update cart attribute without clearing the cart
      try {
        await fetch('/cart/update.js', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({ attributes: { delivery_date: this._toMiamiDate(1), pre_order_reason: 'after_cutoff_time' } }),
        });
      } catch (_) { /* Non-critical — localStorage is already updated */ }

      // Refresh cart UI so the delivery date display reflects the change
      if ( typeof window.refreshCart === 'function' ) window.refreshCart();

      // Notify other components (e.g. header) but skip re-triggering this component
      document.dispatchEvent(new CustomEvent('deliveryConfigSaved', {
        detail:  { ...updated, skipCutoffNotice: true },
        bubbles: true,
      }));
    }

    _loadDeliveryConfig() {
      try {
        const raw = JSON.parse(localStorage.getItem('domaselo_delivery_config'));
        if (!raw) return null;
        const todayIso = this._toMiamiDate(0);
        const d = raw.date;
        // Normalize legacy symbolic strings and stale past dates → today
        if (!d || d === 'today' || d === 'tomorrow' || d < todayIso) {
          const normalized = { ...raw, date: todayIso };
          delete normalized.autoSwitchedFromToday;
          try { localStorage.setItem('domaselo_delivery_config', JSON.stringify(normalized)); } catch (_) {}
          // Repair the cart attribute so Shopify also gets the corrected date
          if (d && d !== 'today' && d !== 'tomorrow' && d < todayIso) {
            fetch('/cart/update.js', {
              method:  'POST',
              headers: { 'Content-Type': 'application/json' },
              body:    JSON.stringify({ attributes: { delivery_date: todayIso, pre_order_reason: '' } }),
            }).catch(() => {});
          }
          return normalized;
        }
        return raw;
      } catch (_) {
        return null;
      }
    }

    // ── Miami time helpers (mirrors delivery-method-selector) ──────

    _toMiamiDate(offsetDays = 0) {
      const now = new Date();
      const miami = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));
      miami.setDate(miami.getDate() + offsetDays);
      const pad = (n) => String(n).padStart(2, '0');
      return `${miami.getFullYear()}-${pad(miami.getMonth() + 1)}-${pad(miami.getDate())}`;
    }

    _nowMiamiMinutes() {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone:  'America/New_York',
        hour:      'numeric',
        minute:    'numeric',
        hourCycle: 'h23',
      }).formatToParts(new Date());

      const h = parseInt(parts.find(p => p.type === 'hour').value);
      const m = parseInt(parts.find(p => p.type === 'minute').value);
      return h * 60 + m;
    }

    // Accepts 24-hour "21:00" or 12-hour "9:00 PM" / "9AM". Returns minutes since midnight.
    _parseTimeToMinutes(str) {
      if ( !str ) return null;
      const h24 = str.trim().match(/^(\d{1,2}):(\d{2})$/);
      if ( h24 ) return parseInt(h24[1]) * 60 + parseInt(h24[2]);
      const h12 = str.trim().match(/^(\d+)(?::(\d+))?\s*(AM|PM)$/i);
      if ( !h12 ) return null;
      let h = parseInt(h12[1]);
      const min = parseInt(h12[2] || '0');
      const period = h12[3].toUpperCase();
      if ( period === 'PM' && h !== 12 ) h += 12;
      if ( period === 'AM' && h === 12 ) h = 0;
      return h * 60 + min;
    }

  }

  if ( typeof customElements.get('cutoff-notice') === 'undefined' ) {
    customElements.define('cutoff-notice', CutoffNotice);
  }

}
